import { Copy, Trash, Users } from 'react-feather';
import { DocumentNode } from 'graphql';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import {
  GetTicketQuery as GetTicket,
  GetTicketsQuery as GetTickets,
  PurchaseStatus,
} from '__generated__/graphql';

import { ContainerWithOptions, Option } from '../../../../common/Common/OptionsDropdown';
import { encodeUrl, mergeUrlParams } from '../../../../common/helpers';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import ActivateTicketMutation from './ActivateTicketMutation';
import DeactivateTicketMutation from './DeactivateTicketMutation';
import DeleteTicketMutation from './DeleteTicketMutation';
import DuplicateTicketMutation from './DuplicateTicketMutation';
import EditTicketForm from './EditTicketForm';
import GetTicketsQuery from './GetTicketsQuery';
import PriceRange from '../../../../common/Common/PriceRange';
import QuantityLabel from '../../../../common/Tickets/QuantityLabel';
import SellableStatusIndicator, { SellableStatus, useSellableStatus } from '../../../Common/SellableStatusIndicator';
import SportIcon from '../../../../common/Common/SportIcon';
import StartDate from '../../../../common/Common/StartDate';
import UI from '../../../../common/UI';
import useConfirmModal from '../../../../common/useConfirmModal';
import useLocale from '../../../../common/useLocale';
import useMediaDevice from '../../../../common/useMediaDevice';
import useMutation from '../../../../api/useMutation';
import useProject from '../../../useProject';

type Event = GetTickets['project']['event'];
type TicketCategory = Event['ticket_categories'][0];
type Ticket = TicketCategory['tickets'][0];

export interface TicketListProps {
  event: Event;
  ticketCategory: TicketCategory;
  activeTicketId?: string;
  ticketData?: GetTicket;
  openAddTicketModal: () => void;
  refetchQueries?: DocumentNode[];
}

const TicketList = ({
  event, ticketCategory, activeTicketId, ticketData, openAddTicketModal,
  refetchQueries = [GetTicketsQuery],
}: TicketListProps) => {
  const { t } = useTranslation();
  const project = useProject();

  return (
    <>
      <UI.HR />
      <UI.FlexContainer
        sc={{
          justifyContent: 'space-between',
          alignItems: [undefined, 'center'],
          direction: ['column', 'row'],
          spaceY: [1, 0],
        }}
        id={`TicketCategory_${ticketCategory.id}`}
      >
        <UI.FlexContainer sc={{ alignItems: 'center' }}>
          <UI.Badge
            sc={{ size: 40, mr: 2, brand: 'gray.100', borderRadius: 'lg' }}
            style={{ boxShadow: 'inset 0 -40px 40px -20px rgba(0, 0, 0, 0.05)' }}
          >
            <UI.Icon sc={{ size: 24 / 14, color: 'gray.600' }}>
              <SportIcon type={ticketCategory.sport_type} />
            </UI.Icon>
          </UI.Badge>
          <UI.Div>
            <UI.H4 sc={{ strong: true }}>
              {ticketCategory.title}
            </UI.H4>
            {ticketCategory.start_date && (
              <UI.Div sc={{ color: 'gray.500' }}>
                <StartDate date={ticketCategory.start_date} />
              </UI.Div>
            )}
          </UI.Div>
        </UI.FlexContainer>
        <UI.Div sc={{ spaceX: 3, mt: [3, 0] }}>
          <UI.Link
            to={`/${project.id}/events/${event.id}/tickets/add${mergeUrlParams({ ticket_category: ticketCategory.id })}`}
            onClick={() => openAddTicketModal()}
            sc={{ secondary: true, color: 'gray.600', fontWeight: 500 }}
          >
            {t('add_ticket')}
          </UI.Link>
          <UI.Link
            to={`/${project.id}/events/${event.id}/tickets/categories/${ticketCategory.id}`}
            sc={{ secondary: true, color: 'gray.600', fontWeight: 500 }}
          >
            {t('settings')}
          </UI.Link>
          {event.ticket_categories.length > 1 && (
            <UI.A
              href={ticketCategory.checkout_url}
              target="_blank"
              rel="noopener noreferrer"
              sc={{ secondary: true, color: 'gray.600', fontWeight: 500 }}
            >
              {t('view_checkout')}
            </UI.A>
          )}
        </UI.Div>
      </UI.FlexContainer>
      <UI.GridContainer sc={{ gutter: 0.5 }}>
        {ticketCategory.tickets.map((ticket) => (
          <UI.CollapsibleNavCard
            to={`/${project.id}/events/${event.id}/tickets/${activeTicketId !== ticket.id ? ticket.id : ''}`}
            open={ticket.id === activeTicketId}
            muted={!!activeTicketId && ticket.id !== activeTicketId}
            preview={(
              <TicketPreview
                ticket={ticket}
                ticketCategory={ticketCategory}
                event={event}
                refetchQueries={refetchQueries}
              />
            )}
            body={ticketData
              ? (
                <EditTicketForm
                  ticket={ticketData.ticket}
                  event={ticketData.event}
                  distanceUnits={ticketData.distanceUnits}
                />
              )
              : <UI.PageLoader />}
            id={`TicketCard_${ticket.id}`}
            key={ticket.id}
          />
        ))}
        {ticketCategory.tickets.length === 0 && (
          <UI.Card sc={{ padding: 8, background: 'gray.50', color: 'gray.500', textAlign: 'center' }}>
            <UI.Div sc={{ maxWidth: [undefined, '60%'] }} style={{ margin: '0 auto' }}>
              <UI.H4 sc={{ mb: 1 }}>
                {t('no_tickets_yet')}
              </UI.H4>
              <Trans i18nKey="empty_ticket_category_actions">
                <UI.Link
                  to={mergeUrlParams({ ticket_category: ticketCategory.id })}
                  onClick={() => openAddTicketModal()}
                />
                <UI.Link to={`/${project.id}/events/${event.id}/tickets/sort`} />
              </Trans>
            </UI.Div>
          </UI.Card>
        )}
      </UI.GridContainer>
    </>
  );
};

interface TicketPreviewProps {
  ticket: Ticket;
  ticketCategory: TicketCategory;
  event: Event;
  refetchQueries?: DocumentNode[];
}

const TicketPreview = ({ ticket, ticketCategory, event, refetchQueries = [] }: TicketPreviewProps) => {
  const { t } = useTranslation();
  const { formatInteger } = useLocale();
  const notifier = useNotifier();
  const device = useMediaDevice();
  const project = useProject();
  const history = useHistory();

  const [activateTicket] = useMutation(ActivateTicketMutation, {
    variables: {
      id: ticket.id,
    },
    optimisticResponse: {
      activateTicket: {
        id: ticket.id,
        active: true,
        __typename: 'Ticket',
      },
    },
    onCompleted: () => notifier.success(t('notifications.ticket_activated')),
  });

  const [deactivateTicket] = useMutation(DeactivateTicketMutation, {
    variables: {
      id: ticket.id,
    },
    optimisticResponse: {
      deactivateTicket: {
        id: ticket.id,
        active: false,
        __typename: 'Ticket',
      },
    },
    onCompleted: () => notifier.success(t('notifications.ticket_deactivated')),
  });

  const toggleStatus = () => {
    if (ticket.active) {
      deactivateTicket();
    } else {
      activateTicket();
    }
  };

  const [duplicateTicket, { loading: duplicating }] = useMutation(DuplicateTicketMutation, {
    variables: {
      id: ticket.id,
    },
    onCompleted: () => {
      notifier.success(t('notifications.ticket_duplicated'));

      history.push(`/${project.id}/events/${event.id}/tickets`);
    },
    onError: () => {
      notifier.error(t('something_went_wrong'));
    },
    refetchQueries,
    awaitRefetchQueries: refetchQueries.length > 0,
  });

  const [deleteTicket, { loading: deleting }] = useMutation(DeleteTicketMutation, {
    variables: {
      id: ticket.id,
    },
    onCompleted: () => {
      notifier.success(t('notifications.ticket_deleted'));

      history.push(`/${project.id}/events/${event.id}/tickets`);
    },
    onError: () => {
      notifier.error(t('something_went_wrong'));
    },
    refetchQueries,
    awaitRefetchQueries: refetchQueries.length > 0,
  });

  const [confirmDelete] = useConfirmModal({
    title: t('delete_name', { name: ticket.title }),
    onConfirm: () => deleteTicket(),
  }, [ticket]);

  const { status } = useSellableStatus(ticket);
  const muted = !ticket.active && status !== SellableStatus.ByInvitation;

  return (
    <ContainerWithOptions
      name={ticket.title}
      renderOptions={() => (
        <>
          <Option
            icon={<Copy />}
            label={t('duplicate')}
            onClick={() => duplicateTicket()}
            disabled={duplicating}
          />
          {ticket.deletable && (
            <Option
              icon={<Trash />}
              label={t('delete')}
              onClick={() => confirmDelete()}
              disabled={deleting}
            />
          )}
        </>
      )}
    >
      <UI.GridContainer sc={{ columns: ['1fr 1fr', '3fr 1fr 1fr 1fr'], gutter: 0.5, alignVertical: 'center' }}>
        <UI.Div sc={{ muted }}>
          <UI.Div>
            <UI.H4 sc={{ inlineBlock: true }}>
              {ticket.title}
            </UI.H4>
            {ticket.units > 1 && (
              <UI.Span sc={{ ml: 1 }}>
                <QuantityLabel>
                  <UI.Icon>
                    <Users />
                  </UI.Icon>
                  {' '}
                  {ticket.units}
                </QuantityLabel>
              </UI.Span>
            )}
          </UI.Div>

          <PriceRange prices={ticket.promotions.map(({ amount }) => ({ amount }))} />
        </UI.Div>

        {!device.isSmall && (
          <>
            <UI.Div sc={{ muted }}>
              <UI.Div sc={{ fontWeight: 500 }}>
                {formatInteger(ticket.sold_count)}
                {ticket.capacity !== null && (
                  <UI.Sup sc={{ muted: true }}>
                    {` / ${formatInteger(ticket.capacity)}`}
                  </UI.Sup>
                )}
              </UI.Div>
              <UI.Link
                to={`/${project.id}/participants?${encodeUrl({
                  event: event.id,
                  tickets: [ticket.id],
                  ticket_categories: [ticketCategory.id],
                  statuses: [PurchaseStatus.complete, PurchaseStatus.incomplete],
                })}`}
                sc={{ secondary: true }}
              >
                {t('registrations', { count: ticket.sold_count })}
              </UI.Link>
            </UI.Div>

            <UI.Div sc={{ muted }}>
              {ticket.invitation_reservations_count > 0 && (
                <>
                  <UI.Div sc={{ fontWeight: 500 }}>
                    {formatInteger(ticket.invitation_reservations_count)}
                  </UI.Div>
                  <UI.Div sc={{ ml: [1, 0] }}>
                    {t('reserved', { count: ticket.invitation_reservations_count })}
                  </UI.Div>
                </>
              )}
            </UI.Div>
          </>
        )}

        <UI.Div sc={{ textAlign: 'right', small: true }}>
          <UI.Toggle checked={ticket.active} onChange={() => toggleStatus()} />

          <UI.Truncate sc={{ color: 'gray.500' }}>
            <SellableStatusIndicator sellable={ticket} />
          </UI.Truncate>
        </UI.Div>
      </UI.GridContainer>
    </ContainerWithOptions>
  );
};

export default TicketList;
